'use client';

import { event_view_item_list } from '.';

// The trigger is per item ("at least 50% visible for 2 seconds") but the payload
// is list-level, so qualified items are collected and flushed as ONE
// `view_item_list` push per list rather than one push per card.
//
// A grid resolves its visible items within a frame or two of each other; this
// window is long enough to gather them into a single event and short enough that
// the push still describes what the shopper is looking at.
const FLUSH_MS = 300;

let pending = new Map();
let flushTimer = null;

// Spec: each item counted once per page view. The key carries the list, the view
// of that list, and the item:
//
//   list   — the same product in two lists on one page reports once for each;
//   view   — `viewKey` changes when a list re-renders with a different result
//            set (a PLP page number, a search query), which is how paginated
//            lists report "once per results page load". It is part of the key
//            rather than a global generation marker, so two lists on one page
//            with different view keys cannot clear each other's state;
//   item   — duplicate DOM nodes for one card (the bundle block renders a mobile
//            and a desktop copy) report only once.
let seenItems = new Set();
let seenOnPath = null;

// Only a route change clears the set, and only to keep it from growing without
// bound. Correctness does not depend on this being called.
export const resetSeenItems = (pathname) => {
  if (seenOnPath === pathname) return;
  seenOnPath = pathname;
  seenItems = new Set();
};

export const queueListItem = (list, viewKey, item) => {
  if (!list?.item_list_id || !item?.item_id) return;

  const key = `${list.item_list_id}::${viewKey ?? ''}::${item.item_id}`;
  if (seenItems.has(key)) return;
  seenItems.add(key);

  const batch = pending.get(list.item_list_id) || { list, items: [] };
  batch.items.push(item);
  pending.set(list.item_list_id, batch);

  if (flushTimer) return;
  flushTimer = setTimeout(flushListItems, FLUSH_MS);
};

export const flushListItems = () => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  const batches = pending;
  pending = new Map();

  batches.forEach(({ list, items }) => {
    if (!items.length) return;
    // Report in list order, not in the order the viewport happened to reveal.
    items.sort((a, b) => a.index - b.index);
    event_view_item_list(list, items);
  });
};
